import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RotateCcw, CheckCircle, XCircle, Shield, AlertTriangle } from "lucide-react";

const formatDate = (d) => d ? new Date(d).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
const statusColors = { pending: "bg-yellow-900/30 text-yellow-400 border-yellow-800/40", approved: "bg-green-900/30 text-green-400 border-green-800/40", rejected: "bg-red-900/30 text-red-400 border-red-800/40" };
const statusLabels = { pending: "Menunggu Persetujuan", approved: "Disetujui", rejected: "Ditolak" };

export default function RefundPage() {
  const { user } = useAuth();
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const canApprove = user?.role === "kepala_cabang" || user?.role === "administrator" || user?.role === "store_manager";

  const fetchRefunds = useCallback(async () => {
    try { const { data } = await api.get("/refunds"); setRefunds(data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRefunds(); }, [fetchRefunds]);

  const approve = async (id) => {
    if (!window.confirm("Setujui refund ini? Tindakan ini tidak bisa dibatalkan.")) return;
    try { await api.put(`/refunds/${id}/approve`); toast.success("Refund disetujui"); fetchRefunds(); } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const openReject = (id) => { setSelectedId(id); setRejectReason(""); setRejectDialog(true); };
  const submitReject = async () => {
    if (!rejectReason.trim()) return toast.error("Alasan penolakan wajib diisi");
    try { await api.put(`/refunds/${selectedId}/reject`, { reason: rejectReason }); toast.success("Refund ditolak"); setRejectDialog(false); fetchRefunds(); } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="refund-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Refund</h1>
        <p className="text-gray-400 text-sm mt-1">Kelola permintaan refund pelanggan</p>
      </div>

      {/* Approval Info Banner */}
      <div className={`mb-6 border px-4 py-3 flex items-center gap-3 ${canApprove ? "border-green-800/40 bg-green-900/10" : "border-yellow-800/40 bg-yellow-900/10"}`}>
        <Shield size={16} className={canApprove ? "text-green-400" : "text-yellow-400"} />
        <div>
          {canApprove ? (
            <p className="text-green-400 text-xs">Anda login sebagai <strong>{user?.role === "kepala_cabang" ? "Kepala Cabang" : user?.role === "store_manager" ? "Store Manager" : "Administrator"}</strong> — Anda memiliki wewenang untuk menyetujui atau menolak refund.</p>
          ) : (
            <p className="text-yellow-400 text-xs">Persetujuan refund bisa dilakukan oleh <strong>Kepala Cabang</strong>, <strong>Store Manager</strong>, atau <strong>Administrator</strong>. Anda login sebagai {user?.role}.</p>
          )}
        </div>
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="refund-table">
        <table className="w-full text-sm"><thead><tr className="border-b border-[#262626]">
          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Pelanggan</th>
          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Tipe</th>
          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Alasan</th>
          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Status</th>
          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Approval</th>
          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Tanggal</th>
          {canApprove && <th className="text-right px-4 py-3 text-xs font-bold uppercase text-gray-500">Aksi</th>}
        </tr></thead><tbody>
          {refunds.length === 0 ? (
            <tr><td colSpan={canApprove ? 7 : 6} className="px-4 py-8 text-center text-gray-500">Belum ada permintaan refund</td></tr>
          ) : refunds.map(r => (
            <tr key={r.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A]" data-testid={`refund-row-${r.id}`}>
              <td className="px-4 py-3">
                <p className="text-white font-semibold text-sm">{r.customer_name}</p>
                {r.transaction_id && <p className="text-gray-500 text-[10px] font-mono">{r.transaction_id}</p>}
                {r.order_id && <p className="text-gray-500 text-[10px] font-mono">{r.order_id}</p>}
              </td>
              <td className="px-4 py-3"><Badge variant="secondary" className="text-[10px] uppercase">{r.type}</Badge></td>
              <td className="px-4 py-3 text-gray-400 max-w-[200px]"><p className="truncate">{r.reason}</p></td>
              <td className="px-4 py-3"><Badge className={`${statusColors[r.status] || ""} text-[10px]`}>{statusLabels[r.status] || r.status}</Badge></td>
              <td className="px-4 py-3">
                {r.approved_by ? (
                  <div>
                    <p className="text-white text-xs">{r.approved_by}</p>
                    <p className="text-gray-500 text-[10px]">{r.approved_role === "kepala_cabang" ? "Kepala Cabang" : r.approved_role}</p>
                    {r.reject_reason && <p className="text-red-400 text-[10px] mt-0.5">Alasan: {r.reject_reason}</p>}
                  </div>
                ) : (
                  <span className="text-gray-600 text-xs">Menunggu Kepala Cabang</span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(r.created_at)}</td>
              {canApprove && (
                <td className="px-4 py-3 text-right">
                  {r.status === "pending" && (
                    <div className="flex justify-end gap-2">
                      <Button onClick={() => approve(r.id)} className="bg-green-800 hover:bg-green-700 text-white rounded-none h-7 text-[10px] px-2.5" data-testid={`approve-${r.id}`}><CheckCircle size={11} className="mr-1" />Setujui</Button>
                      <Button onClick={() => openReject(r.id)} className="bg-red-800 hover:bg-red-700 text-white rounded-none h-7 text-[10px] px-2.5" data-testid={`reject-${r.id}`}><XCircle size={11} className="mr-1" />Tolak</Button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody></table>
      </div>

      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" /> Tolak Refund</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Berikan alasan penolakan (wajib)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" placeholder="Alasan penolakan..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
            <Button onClick={submitReject} disabled={!rejectReason.trim()} className="w-full bg-red-800 hover:bg-red-700 text-white font-bold uppercase rounded-none h-10 disabled:opacity-40" data-testid="reject-submit-btn">Tolak Refund</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
